/** Tipi e etichette del monitoraggio carichi, allineati alle risposte dell'API. */

import {
  ETICHETTA_ACWR,
  type EsitoAcwr,
  type FamigliaAttributo,
  type FinestraCarico,
  type GruppoConfronto,
  type LetturaAcwr,
  type RuoloGiocatore,
} from '@calcio/shared';

export const ETICHETTA_FINESTRA: Record<FinestraCarico, string> = {
  settimana: 'Settimana',
  mese: 'Mese',
  stagione: 'Stagione',
};

export interface GiocatoreCaricoSquadra {
  id: string;
  nome: string;
  cognome: string;
  numero: number | null;
  ruolo: RuoloGiocatore;
  gruppoConfronto: GruppoConfronto;
  doseUa: number;
  acwr: EsitoAcwr;
  alertScostamento: boolean;
  serieSottoTarget: boolean;
  serieDose: number[];
  serieDosePerFamiglia: Record<FamigliaAttributo, number[]>;
}

export interface CarichiSquadra {
  da: string;
  a: string;
  doseUa: number;
  acwr: EsitoAcwr;
  /** Quanti giocatori hanno tre sedute consecutive sotto target. */
  sottoTarget: number;
  asse: string[];
  attributi: AttributoSquadra[];
  portieri: GiocatoreCaricoSquadra[];
  movimento: GiocatoreCaricoSquadra[];
}

export interface AttributoSquadra {
  attributoId: string;
  nome: string;
  famiglia: string;
  ua: number;
}

export interface AttributoCarico {
  attributoId: string;
  nome: string;
  famiglia: string;
  centesimiUa: number;
  ua: number;
  percentuale: number;
}

export interface CarichiGiocatore {
  da: string;
  a: string;
  ruolo: RuoloGiocatore;
  gruppoConfronto: GruppoConfronto;
  doseUa: number;
  acwr: EsitoAcwr;
  serieSottoTarget: boolean;
  serieGiornaliera: { giorno: string; doseUa: number }[];
  asse: string[];
  serieDose: number[];
  serieDosePerFamiglia: Record<FamigliaAttributo, number[]>;
  /** Gli attributi con carico nel periodo, dal più caricato. */
  attributi: AttributoCarico[];
  dettaglio: {
    sedute: {
      allenamentoId: string;
      giorno: string;
      squadraStagioneId: string;
      delta: number | null;
      doseUa: number;
      blocchi: {
        bloccoId: string;
        nome: string;
        rpeTarget: number;
        rpeEseguito: number;
        minutiEseguiti: number;
        scostamento: number;
      }[];
    }[];
    partite: { partitaId: string; giorno: string; squadraStagioneId: string; doseUa: number }[];
  };
}

export interface CaricoPartitaLetto {
  esiste: boolean;
  partita: { id: string; stato: string };
  scheda: { id: string; nome: string; tipo: string } | null;
  /** In ordine di cognome e nome. */
  partecipanti: {
    giocatoreId: string;
    nome: string;
    cognome: string;
    numero: number | null;
    ruoloPreferito: RuoloGiocatore;
    minutiPartecipazione: number;
  }[];
  log: {
    giocatoreId: string;
    rpeEseguito: number;
    minutiRegistrati: number;
    minutiPartecipazione: number;
    minutiDiversi: boolean;
  }[];
}

export function etichettaAcwr(esito: EsitoAcwr): string {
  if (esito.stato === 'ok') {
    return `${esito.valore.toFixed(2)} · ${ETICHETTA_ACWR[esito.lettura]}`;
  }
  return ETICHETTA_ACWR[esito.stato];
}

/** La sola lettura, senza il valore: il valore sta nel tassello della riga di V68. */
export function letturaAcwr(esito: EsitoAcwr): string {
  return ETICHETTA_ACWR[esito.stato === 'ok' ? esito.lettura : esito.stato];
}

/** Tassello e badge della riga di V68, conteggi nel titolo delle sue card e badge di V69: i colori degli stati del giocatore, dal verde al rosso. */
export const COLORE_LETTURA: Record<LetturaAcwr, string> = {
  sotto: 'bg-stato-affaticato text-black',
  zona: 'bg-stato-disponibile text-white',
  attenzione: 'bg-stato-recupero text-black',
  rischio: 'bg-stato-infortunato text-white',
};

export function coloreAcwr(esito: EsitoAcwr): string {
  return esito.stato === 'ok' ? COLORE_LETTURA[esito.lettura] : 'bg-muted text-muted-foreground';
}
